import type { MidiNote, StemManifest } from "../types";

export type AcousticDrumVoice = NonNullable<MidiNote["voice"]>;
type SandboxKit = NonNullable<StemManifest["sandboxKit"]>;

export class AcousticDrumSampler {
  private buffers = new Map<AcousticDrumVoice, AudioBuffer>();
  private sources = new Set<AudioBufferSourceNode>();
  private occurrences = new Map<string, number>();
  private output: GainNode;
  private voiceGains = new Map<AcousticDrumVoice, GainNode>();
  private loadPromise?: Promise<void>;
  private enabled = false;
  private levelDb = 0;

  constructor(
    private context: AudioContext,
    private manifestBase: string,
    private kit: SandboxKit,
    destination: AudioNode,
    private outputTrimDb = 0,
  ) {
    this.output = context.createGain();
    this.output.gain.value = 0;
    this.output.connect(destination);
    for (const voice of Object.keys(kit.voices) as AcousticDrumVoice[]) {
      const gain = context.createGain();
      gain.gain.value = 1;
      gain.connect(this.output);
      this.voiceGains.set(voice, gain);
    }
  }

  async load(): Promise<void> {
    if (this.loadPromise) return this.loadPromise;
    this.loadPromise = Promise.all(
      Object.entries(this.kit.voices).map(async ([voice, definition]) => {
        const response = await fetch(this.manifestBase + definition.file);
        if (!response.ok) throw new Error(`${definition.file} returned ${response.status}`);
        const decoded = await this.context.decodeAudioData(await response.arrayBuffer());
        this.buffers.set(voice as AcousticDrumVoice, decoded);
      }),
    ).then(() => {
      this.setEnabled(this.enabled);
    }).catch((error: unknown) => {
      this.loadPromise = undefined;
      throw error;
    });
    return this.loadPromise;
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    this.applyOutputGain();
    if (!enabled) this.stop();
  }

  setLevelDb(levelDb: number): void {
    this.levelDb = levelDb;
    this.applyOutputGain();
  }

  setVoiceLevelDb(voice: AcousticDrumVoice, levelDb: number): void {
    const gain = this.voiceGains.get(voice);
    if (!gain) return;
    gain.gain.cancelScheduledValues(this.context.currentTime);
    gain.gain.setTargetAtTime(
      10 ** (levelDb / 20),
      this.context.currentTime,
      0.012,
    );
  }

  private applyOutputGain(): void {
    const gainDb = this.kit.outputGainDb + this.outputTrimDb + this.levelDb;
    this.output.gain.cancelScheduledValues(this.context.currentTime);
    this.output.gain.setTargetAtTime(
      this.enabled ? 10 ** (gainDb / 20) : 0,
      this.context.currentTime,
      0.008,
    );
  }

  hit(voice: AcousticDrumVoice, level: number, at?: number): void {
    const buffer = this.buffers.get(voice);
    if (!buffer || !this.enabled) return;
    const definition = this.kit.voices[voice];
    const closestLevel = definition.samples.reduce(
      (closest, sample) => (
        Math.abs(sample.level - level) < Math.abs(closest - level)
          ? sample.level
          : closest
      ),
      definition.samples[0].level,
    );
    const choices = definition.samples.filter(
      (sample) => sample.level === closestLevel,
    );
    const occurrenceKey = `${voice}:${closestLevel}`;
    const occurrence = this.occurrences.get(occurrenceKey) ?? 0;
    this.occurrences.set(occurrenceKey, occurrence + 1);
    const sample = choices[occurrence % choices.length];
    const source = this.context.createBufferSource();
    source.buffer = buffer;
    source.connect(this.voiceGains.get(voice)!);
    this.sources.add(source);
    source.onended = () => {
      this.sources.delete(source);
      source.disconnect();
    };
    source.start(
      at ?? this.context.currentTime,
      sample.offsetSeconds,
      sample.durationSeconds,
    );
  }

  stop(): void {
    for (const source of this.sources) {
      try { source.stop(); } catch { /* already stopped */ }
      source.disconnect();
    }
    this.sources.clear();
    this.occurrences.clear();
  }

  destroy(): void {
    this.stop();
    for (const gain of this.voiceGains.values()) gain.disconnect();
    this.voiceGains.clear();
    this.output.disconnect();
  }
}
